/**
 * SyncStatusPill — green/red status chip + popup with per-source sync times.
 *
 * Mirrors the mobile header's data-sync widget so the desktop sidebar gets
 * the same at-a-glance freshness indicator. Renders just an icon (16x16) so
 * it slots cleanly into the `sidenav-actions` row.
 *
 * - Green when EVERY tracked sync is < 2 hours old.
 * - Red + pulsing dot when ANY tracked sync is stale.
 * - Popup is a `position: fixed` panel placed relative to the trigger
 *   button so it works regardless of the sidebar's transform context.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { ClockCounterClockwise, ArrowsClockwise } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const STALE_MS = 2 * 60 * 60 * 1000; // 2 hours

export default function SyncStatusPill({ placement = 'right' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const fetchSync = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/last-sync`);
      setData(data);
    } catch (e) {
      // Endpoint missing or auth error — silently keep the pill in unknown state.
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll on mount + every 90 seconds while the tab is open. Cheap enough.
  useEffect(() => {
    fetchSync();
    const id = setInterval(fetchSync, 90 * 1000);
    return () => clearInterval(id);
  }, [fetchSync]);

  // Position the popup whenever it opens / on resize / on scroll.
  useEffect(() => {
    if (!open) return;
    const position = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      // Pop to the right of the sidebar by default; if it would overflow
      // the viewport, flip to the left.
      const popupW = 304;
      const margin = 12;
      let left = r.right + 8;
      if (left + popupW + margin > window.innerWidth) {
        left = Math.max(margin, r.left - popupW - 8);
      }
      // Try to keep the popup vertically centered on the trigger, clamped to
      // the viewport.
      const popupApproxH = 360;
      let top = r.top - popupApproxH / 2 + r.height / 2;
      top = Math.max(margin, Math.min(top, window.innerHeight - popupApproxH - margin));
      setPopupPos({ top, left });
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      const pop = document.getElementById('cs-sync-popup');
      if (pop && pop.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Treat the whole pill as stale when any sync is stale OR no data exists.
  const isStale = (() => {
    if (!data?.all_syncs?.length) return false; // unknown = neutral, not red
    return data.all_syncs.some((s) => s?.iso && (Date.now() - new Date(s.iso).getTime()) > STALE_MS);
  })();

  const color = isStale ? '#EF4444' : '#10B981';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Data sync status"
        aria-label="Data sync status"
        data-testid="sidenav-sync-pill"
        className="sidenav-action relative"
        style={{
          color,
          background: open ? `${color}1A` : undefined,
        }}
      >
        <ClockCounterClockwise size={15} weight="bold" />
        {/* Status dot — top-right corner */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 3,
            right: 3,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: color,
            boxShadow: `0 0 0 2px var(--cs-bg-surface), 0 0 6px ${color}99`,
            animation: isStale ? 'cs-sync-pulse 1.6s ease-in-out infinite' : undefined,
          }}
        />
      </button>

      {open && createPortal(
        <SyncPopup
          pos={popupPos}
          isStale={isStale}
          data={data}
          loading={loading}
          onRefresh={fetchSync}
        />,
        document.body,
      )}

      <style>{`
        @keyframes cs-sync-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.35); opacity: 0.55; }
        }
      `}</style>
    </>
  );
}

function SyncPopup({ pos, isStale, data, loading, onRefresh }) {
  const accent = isStale ? '#EF4444' : '#10B981';
  return (
    <div
      id="cs-sync-popup"
      data-testid="sidenav-sync-popup"
      role="dialog"
      aria-label="Data sync status"
      className="rounded-2xl shadow-2xl overflow-hidden"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 304,
        maxHeight: '70vh',
        background: 'var(--cs-bg-surface, #0F0F0F)',
        border: '1px solid var(--cs-border, #262626)',
        zIndex: 80,
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
      }}
    >
      {/* Top accent line */}
      <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />

      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between gap-3"
        style={{ borderBottom: '1px solid var(--cs-border)' }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${accent}14`, border: `1px solid ${accent}33` }}
          >
            <ClockCounterClockwise size={15} weight="bold" style={{ color: accent }} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-[0.18em] truncate" style={{ color: 'var(--cs-text)' }}>
              Sync Status
            </div>
            <div className="text-[10px] font-bold" style={{ color: 'var(--cs-text-muted)' }}>
              Auto-refreshes every 90s
            </div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider shrink-0"
          style={{
            background: `${accent}14`,
            color: accent,
            border: `1px solid ${accent}33`,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: accent,
              animation: isStale ? 'cs-sync-pulse 1.6s ease-in-out infinite' : undefined,
            }}
          />
          {isStale ? 'Stale' : 'Fresh'}
        </span>
      </div>

      {/* Body */}
      <div
        className="overflow-y-auto"
        style={{ maxHeight: 'calc(70vh - 96px)', touchAction: 'pan-y' }}
      >
        {data?.all_syncs?.length ? (
          <div className="py-1">
            {data.all_syncs.map((s, i) => {
              const entryStale = s?.iso ? (Date.now() - new Date(s.iso).getTime()) > STALE_MS : false;
              const c = entryStale ? '#EF4444' : '#10B981';
              return (
                <div
                  key={s.name || i}
                  className="flex items-center gap-3 px-4 py-2.5 mx-1.5 rounded-lg hover:bg-[var(--cs-bg-hover)] transition-colors"
                  data-testid={`sidenav-sync-row-${i}`}
                >
                  <span
                    aria-hidden
                    style={{ width: 6, height: 6, borderRadius: '50%', background: c, boxShadow: `0 0 6px ${c}80`, flex: 'none' }}
                  />
                  <span className="text-xs truncate flex-1" style={{ color: 'var(--cs-text)' }}>{s.name}</span>
                  <span
                    className="text-[11px] font-mono font-bold tabular-nums whitespace-nowrap"
                    style={{ color: entryStale ? '#EF4444' : 'var(--cs-text-muted)' }}
                  >
                    {s.time || '—'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-xs" style={{ color: 'var(--cs-text-muted)' }}>
            Loading sync data…
          </div>
        ) : (
          <div className="py-8 text-center">
            <ClockCounterClockwise size={22} className="mx-auto mb-2" style={{ color: 'var(--cs-text-muted)' }} />
            <p className="text-xs" style={{ color: 'var(--cs-text-muted)' }}>No sync data yet</p>
          </div>
        )}
      </div>

      {/* Footer — manual refresh */}
      <button
        onClick={onRefresh}
        disabled={loading}
        data-testid="sidenav-sync-refresh"
        className="w-full px-4 py-2.5 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider transition-all hover:opacity-90"
        style={{
          borderTop: '1px solid var(--cs-border)',
          color: accent,
          background: `${accent}08`,
        }}
      >
        <ArrowsClockwise size={13} weight="bold" className={loading ? 'animate-spin' : ''} />
        {loading ? 'Refreshing…' : 'Refresh now'}
      </button>
    </div>
  );
}
